import type {
  IQuote,
  SessionMessageModel,
  WS_DATA_TYPE_TEXT,
  WS_MSG_DATA_TYPE,
  WS_MSG_MULTIPLE_DATA
} from '@mindverse/accessor-open';
import { SENDER_TYPE, SessionMessageType } from '@mindverse/accessor-open';

export enum MessageItemType {
  client = SENDER_TYPE.client,
  umm = SENDER_TYPE.umm,
  system = 'system' // 用于处理分割线
}

export enum EMessageAction {
  NORMAL = 'normal',
  REVOKED = 'revoked',
  SHIELDED = 'shielded'
}

export default interface MessageItem {
  type: SessionMessageType;
  sender: MessageItemType;
  multipleData?: WS_MSG_MULTIPLE_DATA[]; // 接收的数据
  data: WS_DATA_TYPE_TEXT; // 发送的数据
  dataType?: WS_MSG_DATA_TYPE;
  timestamp?: number;
  dividerContent?: string;
  content?: string;
  messageId: string;
  seqId: string;
  sendUserId: string;
  sources?: string[];
  index?: number;
  subCode?: string;
  actionStatus?: EMessageAction;
  messageStatus?: 'READ' | 'UNREAD';
}

export interface IHint {
  type: SessionMessageType;
  messageId: SessionMessageModel['messageId'];
  multipleData: SessionMessageModel['multipleData'];
  seqId: string;
  sender: MessageItemType;
  sendUserId: string;
}

export interface IRecommend {
  type: SessionMessageType;
  messageId: SessionMessageModel['messageId'];
  multipleData: SessionMessageModel['multipleData'];
  sender: MessageItemType;
  sendUserId: string;
  seqId: string;
}

export type MessageType = MessageItem | IHint | IRecommend;

export function transformMsg(msg: SessionMessageModel): MessageType | undefined {
  if (msg.type === SessionMessageType.hint) {
    return transformNewHint(msg);
  }

  if (msg.type === SessionMessageType.recommend) {
    return transformNewRecommend(msg);
  }

  if (msg.type === SessionMessageType.msg) {
    return transformNewMsg(msg);
  }

  if (msg.type === SessionMessageType.sys) {
    return transformNewMsg(msg);
  }

  return undefined;
}

// 将收到的消息进行转换
export function transformNewMsg(msg: SessionMessageModel): MessageItem {
  return {
    ...msg,
    actionStatus: msg.actionStatus ?? EMessageAction.NORMAL,
    sender:
      msg.type === SessionMessageType.sys
        ? MessageItemType.system
        : msg.sender === SENDER_TYPE.umm
          ? MessageItemType.umm
          : MessageItemType.client,
    data: msg.sender === SENDER_TYPE.client ? (msg.data as WS_DATA_TYPE_TEXT) : { content: '' }
  };
}

export function transformNewHint(msg: SessionMessageModel): IHint {
  return {
    type: msg.type,
    messageId: msg.messageId,
    multipleData: msg.multipleData,
    seqId: msg.seqId,
    sender: msg.sender === SENDER_TYPE.umm ? MessageItemType.umm : MessageItemType.client,
    sendUserId: msg.sendUserId
  };
}

export function transformNewRecommend(msg: SessionMessageModel): IRecommend {
  return {
    type: msg.type,
    messageId: msg.messageId,
    multipleData: msg.multipleData,
    seqId: msg.seqId,
    sender: msg.sender === SENDER_TYPE.umm ? MessageItemType.umm : MessageItemType.client,
    sendUserId: msg.sendUserId
  };
}

export function transformSplitHintMsg(msg: SessionMessageModel[]): MessageType[] {
  const result = msg
    .filter((item) => {
      return (
        item.type === SessionMessageType.hint ||
        item.type === SessionMessageType.msg ||
        item.type === SessionMessageType.recommend
      );
    })
    .map((item) => {
      if (item.type === SessionMessageType.hint) {
        return transformNewHint(item);
      }

      if (item.type === SessionMessageType.recommend) {
        return transformNewRecommend(item);
      }

      return transformNewMsg(item);
    });

  return result;
}

/**
 * 用于内部展示的铺平后的逻辑
 */
export interface ChatListItem {
  type: MessageItemType; // 消息类型是接受还是发送
  singleDataType: WS_MSG_DATA_TYPE; // 单条消息的类型
  msgBubble?: boolean;
  content: string;
  quote?: IQuote;
  timestamp?: number;
  files?: string[];
  sources: string[];
  messageId?: string;
}
